/**
 * EvalsPage — hosted eval scorecards across the four C02D dimensions.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05B. Renders C02D's
 * `compute_scorecards(...)` output (via the `GET /v1/evals/scorecards` route
 * in `auteur_cloud/api/routes/evals.py` — see `api/evals.ts`) for the model,
 * executor, prompt, and repository dimensions.
 *
 * Cockpit rules preserved here:
 *
 * - Every row renders its `evidence_weight` (`thin` / `adequate` / `strong`)
 *   — C02D's named policy thresholds — so a three-attempt scorecard is never
 *   presented as confidently as a hundred-attempt one.
 * - `raw_score_mean` may be `null` on mixed scales (`raw_scale === 'mixed'`);
 *   the row then shows the normalized mean, which is the comparable figure,
 *   and states that the raw mean is mixed-scale rather than printing a zero.
 * - Opaque CLI runs remain opaque: this page renders aggregates only. No
 *   payload field names per-run content, because C02D's `Scorecard.as_dict()`
 *   returns none.
 * - Missing telemetry renders as unavailable, never zero: an empty or
 *   unreachable scorecard set renders the unavailable state.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import { useEffect, useState } from 'react';
import { fetchScorecards, type ScorecardPayload } from '../../api/evals';

const DIMENSIONS: ('model' | 'executor' | 'prompt' | 'repository')[] = [
  'model',
  'executor',
  'prompt',
  'repository',
];

function ScorecardRow({ card }: { card: ScorecardPayload }) {
  const normalized =
    card.normalized_score_mean === null
      ? 'Unavailable'
      : card.normalized_score_mean.toFixed(3);
  const raw =
    card.raw_score_mean === null
      ? card.raw_scale === 'mixed'
        ? 'Mixed scales'
        : 'Unscored'
      : `${card.raw_score_mean.toFixed(3)} (${card.raw_scale ?? 'scale unstated'})`;
  const costPerResult =
    card.cost_per_trusted_green_usd === null
      ? 'Unavailable'
      : `$${card.cost_per_trusted_green_usd.toFixed(4)}`;

  return (
    <tr data-weight={card.evidence_weight}>
      <td>{card.subject_key}</td>
      <td>{card.sample_count}</td>
      <td>{raw}</td>
      <td>{normalized}</td>
      <td>{card.trusted_green_rate === null ? 'Unavailable' : `${(card.trusted_green_rate * 100).toFixed(1)}%`}</td>
      <td>{costPerResult}</td>
      <td>{card.evidence_weight}</td>
    </tr>
  );
}

export function EvalsPage() {
  const [dimension, setDimension] = useState<(typeof DIMENSIONS)[number]>('model');
  const [cards, setCards] = useState<ScorecardPayload[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCards(null);
    setUnavailable(false);
    fetchScorecards(dimension)
      .then((result) => {
        if (!cancelled) setCards(result);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dimension]);

  return (
    <section className="evals-page" data-state={unavailable ? 'unavailable' : cards ? 'ready' : 'loading'}>
      <h1>Evals</h1>
      <div className="dimension-tabs">
        {DIMENSIONS.map((item) => (
          <button
            key={item}
            type="button"
            className={item === dimension ? 'active' : ''}
            onClick={() => setDimension(item)}
          >
            {item}
          </button>
        ))}
      </div>

      {unavailable ? (
        <p>
          Unavailable — the evals service is not reachable from this deployment
          yet. No scorecard is fabricated.
        </p>
      ) : cards === null ? (
        <p>Loading scorecards…</p>
      ) : cards.length === 0 ? (
        <p>
          No receipts for the {dimension} dimension yet. This is an empty set,
          not a zero score.
        </p>
      ) : (
        <table className="scorecard-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Samples</th>
              <th>Raw mean</th>
              <th>Normalized mean</th>
              <th>Trusted-green rate</th>
              <th>Cost per trusted result</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((card) => (
              <ScorecardRow key={`${card.subject_key}/${card.slice}`} card={card} />
            ))}
          </tbody>
        </table>
      )}
      <p className="page-note">
        Evidence weight is C02D&apos;s named policy threshold, not a statistical
        claim. Mixed-scale rows keep both raw and normalized values; the raw
        mean is withheld there, never guessed.
      </p>
    </section>
  );
}
